import { useFormik } from "formik";
import * as Yup from "yup";
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { TabView, TabPanel } from 'primereact/tabview';
import { userOption } from '../../models/orders/order-list-model';
import { useState, useEffect, useRef } from "react";
import orderServices from '../../services/orders/orderservice';
import ComplaintService from "../../services/complaint/ComplaintService";
import { Toast } from "primereact/toast";
import { ProductDetails, InvoiceNoDetails } from "../../models/complaints/complaint-model";
import { CascadeSelect, CascadeSelectChangeEvent } from "primereact/cascadeselect";
import { useNavigate } from 'react-router-dom';
import {ImageTemplateDemo} from "../images/file-upload-template";

interface YearOption {
  name: string;
  code: string;
  months: MonthOption[];
}

interface MonthOption {
  name: string;
  code: string;
  invoices: InvoiceOption[];
}

interface InvoiceOption {
  name: number | string;
  code: number | string;
  invoice: InvoiceNoDetails;
}

interface EditComplaintProps {
  isUpdate: boolean;
  handleOnSuccess: () => void;
  updateComplaintInfo: any;
}

const ComplaintRegister: React.FC<EditComplaintProps> = ({ isUpdate, handleOnSuccess }) => {

  const [activeTab, setActiveTab] = useState(0);
  const blankUserDetail: userOption[] = [{ userID: 0, name: '', primaryContactNbr: ''}];
  const [orderUsers, setOrderUsers] = useState<userOption[]>(blankUserDetail);
  const [orderUser, setOrderUser] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [orderInvoices, setOrderInvoices] = useState<InvoiceNoDetails[]>([]);
  const [selectedInvoiceNo, setSelectedInvoiceNo] = useState<any>();
  const [imageFile, setImageFile] = useState<any>();
  const blankProductDetails: ProductDetails[] = [{ productId: 0, name: '', quantity: 0, orderInvoiceDetailID: 0 }];
  const [products, setProducts] = useState<ProductDetails[]>(blankProductDetails);
  const [orderProduct, setOrderProduct] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<any>();
  const toast = useRef<Toast>(null);
  const [isProductSubmitted, setIsProductSubmitted] = useState(false);
  const [isServiceSubmitted, setIsServiceSubmitted] = useState(false);
  const [loginUser, setLoginUser] = useState<any>();
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [invoiceHierarchy, setInvoiceHierarchy] = useState<YearOption[]>([]);
  const [categories, setCategories] = useState<any>("");
  const [containerType,setContainerType] = useState<string>("Complaints");
  const [selectedInvoiceDetails,setselectedInvoiceDetails] = useState<any>()
  const navigate = useNavigate();
  
  const severityItems = [
    { name: "High", severityID: 1 },
    { name: "Medium", severityID: 2 },
    { name: "Low", severityID: 3}
  ];

  // Product Complaint Form
  const productInitialValues = {
    customerId: null as number | null,
    invoiceId: null as InvoiceNoDetails | null,
    productId: null as number | null,
    categoryId: 0,
    description: "",
    image: null as any | null,
    containerType: null as string | null,
    severity: null as string | null,
    remarks: null as string | null,
    customerSupportAction: null as string | null,
  };

   const productValidationSchema = Yup.object({
    customerId: Yup.number().required("Customer is required"),
    invoiceId: Yup.number().required("Invoice is required"),
    productId: Yup.number().required("Product is required"),
    description: Yup.string().required("Description is required"),
  });

   const productFormik = useFormik({
    initialValues: productInitialValues,
    validationSchema: productValidationSchema,
    onSubmit: async (data) => {
      if (isProductSubmitted) return;
      setIsProductSubmitted(true);
      const complaint = {
        CustomerId: data.customerId,
        InvoiceId: selectedInvoiceDetails?.orderInvoiceID ?? null,
        ProductId: data.productId,
        CategoryId: data.categoryId,
        Description: data.description,
        Image: data.image,
        ContainerType : containerType,
        Severity: data.severity === '' || data.severity === 'null' ? null : data.severity,
        Remarks: data.remarks === '' || data.remarks === 'null' ? null : data.remarks,
        CustomerSupportAction: data.customerSupportAction,
      };

      try {
        const response = await ComplaintService.CreateProductComplaint(complaint);
        if (response.isSuccess) {
          showMessage('Success', 'Product complaint created successfully', 'success');
          navigate('/complaint-main');
          productFormik.resetForm();
          setImageFile(null);
          handleOnSuccess();
        } else {
          showMessage('Error', response.message, 'error');
        }
      } catch (error) {
        console.error("Error during product complaint submission", error);
        showMessage('Error', 'Failed to create product complaint', 'error');
      } finally {
        setIsProductSubmitted(false);
      }
    },
  });

  // Service Complaint Form
  const serviceInitialValues = {
    customerId: null as number | null,
    description: "",
    image: null as any | null,
    severity: null as string | null,
    remarks: null as string | null,
    customerSupportAction: null as string | null,
  };

  const serviceValidationSchema = Yup.object({
    customerId: Yup.number().required("Please select user info"),
    description: Yup.string().required("Description is required"),
  });

  const serviceFormik = useFormik({
    initialValues: serviceInitialValues,
    validationSchema: serviceValidationSchema,
    onSubmit: async (data) => {
      if (isServiceSubmitted) return;
      setIsServiceSubmitted(true);

      const complaint = {
        CustomerId: data.customerId,
        Description: data.description,
        Image: data.image,
        Severity: data.severity,
        Remarks: data.remarks,
        CustomerSupportAction: data.customerSupportAction,
        CreatedBy: loginUser?.userID || 0,
        CreatedDate: new Date().toISOString(),
        ContainerType : containerType,
      };

      try {
        console.log('Submitting service complaint:', complaint);
        const response = await ComplaintService.CreateServiceComplaint(complaint);
        console.log('Service complaint response:', response);
        
        if (response.isSuccess) {
          showMessage('Success', 'Service complaint created successfully', 'success');
           navigate('/complaint-main');
          serviceFormik.resetForm();
          setImageFile(null);
          handleOnSuccess();
        } else {
          showMessage('Error', response.message, 'error');
        }
      } catch (error) {
        console.error("Error during service complaint submission", error);
      } finally {
        setIsServiceSubmitted(false);
      }
    },
  });

  const formatDate = (dateInput: string | number | Date): string => {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    if (orderUsers && orderUsers.length > 0) {
      setOrderUser(orderUsers.map((user) => ({
        userID: user.userID,
        detail: user.userID !== 0 ? `${user.name} , ${user.primaryContactNbr}` : "",
      })));
    }
    if (products && products.length > 0) {
      setOrderProduct(products.map((product) => ({
        productId: product.productId,
        detail: product.productId !== 0 ? `${product.name}, Quantity - ${product.quantity}` : "",
      })));
    }

    const LoginDetails = localStorage.getItem('user');
    if (LoginDetails != null) {
      setLoginUser(JSON.parse(LoginDetails));
    }
  }, [orderUsers, products]);

  const handlePhoneDropdownChange = async (e: DropdownChangeEvent) => {
    const selectedUser = e.value;
    setSelectedUser(selectedUser);
    
    productFormik.setFieldValue("customerId", selectedUser);
    serviceFormik.setFieldValue("customerId", selectedUser);
    
    const invoices = await ComplaintService.getUserInvoicesAll(selectedUser);
    setOrderInvoices(invoices);
    const invoiceHierarchyData = structureInvoiceData(invoices);
    setInvoiceHierarchy(invoiceHierarchyData);
  };

  const structureInvoiceData = (invoices: InvoiceNoDetails[]): YearOption[] => {
    const yearsMap = new Map<string, Map<string, InvoiceNoDetails[]>>();

    invoices.forEach(invoice => {
      const invoiceDate = new Date(invoice.invoiceDate);
      const year = invoiceDate.getFullYear().toString();
      const month = invoiceDate.toLocaleString('default', { month: 'long' });
      
      if (!yearsMap.has(year)) {
        yearsMap.set(year, new Map<string, InvoiceNoDetails[]>());
      }
      
      const monthsMap = yearsMap.get(year)!;
      if (!monthsMap.has(month)) {
        monthsMap.set(month, []);
      }
      
      monthsMap.get(month)!.push(invoice);
    });

    const result: YearOption[] = [];
    
    yearsMap.forEach((monthsMap, year) => {
      const months: MonthOption[] = [];
      
      monthsMap.forEach((monthInvoices, monthName) => {
        const invoices: InvoiceOption[] = monthInvoices.map(invoice => ({
          name: `${invoice.invoiceNo} - ${formatDate(invoice.invoiceDate)}`, 
          code: invoice.invoiceNo, 
          invoice: invoice        
        }));
        
        months.push({
          name: monthName,
          code: monthName,
          invoices: invoices
        });
      });
      
      result.push({
        name: year,
        code: year,
        months: months
      });
    });

    return result;
  };

  const handlePhoneDropdownFilter = (e: DropdownFilterEvent) => {
    if (e.filter.length > 2) {
      orderServices.getOrderUsers(e.filter).then((response: userOption[]) => {
        if (response.length > 0) {
          setOrderUsers(response);
        }
      });
    }
  };

 const handleInvoiceChange = async (e: CascadeSelectChangeEvent) => {
    if (e.value && e.value.invoice) {
      const selectedInvoice = e.value.invoice;
      setselectedInvoiceDetails(selectedInvoice);
      productFormik.setFieldValue("invoiceId", selectedInvoice.invoiceNo);
      const products = await ComplaintService.getInvoicedproducts(selectedInvoice.orderInvoiceID);
      setProducts(products);
      setSelectedInvoiceNo(selectedInvoice.invoiceNo);
    }
  };


  const handleProductChange = async (e: DropdownChangeEvent) => {
    const category = await ComplaintService.getProductCategory(e.value);
    productFormik.setFieldValue("productId", e.value);
    productFormik.setFieldValue("categoryId", category[0]?.categoryID || 0);
    setSelectedProducts(e.value);
    setCategories(category);
    productFormik.setTouched({ ...productFormik.touched, productId: false }); 
  };

  const handleSeverityChange = (e: DropdownChangeEvent, formik: any) => {
    formik.setFieldValue("severity", e.value);
  };

  const showMessage = (label: string, message: string, severity: 'success' | 'error' | 'info' | 'warn') => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const fieldHoverStyle = (fieldName: string) => ({
    border: hoveredField === fieldName ? '2px solid #6366F1' : '1px solid #ced4da',
    borderRadius: '6px',
    transition: 'border 0.2s ease-in-out',
    width: '100%',
    minHeight: '40px'
  });

  // ✅ Responsive container
  const formContainerStyle = {
    width: '100%',
    maxWidth: '960px',
    margin: '1.5% auto', 
    padding: '16px',
    border: '2px solid gray',
    borderRadius: '6px'
  };

  const handelfileUpload = (query:any) => {
    const file = query;
    setImageFile(file);
    productFormik.setFieldValue("image", file);
    serviceFormik.setFieldValue("image", file);
  }

  const handelfileSizeExceeded = (message:string) => {
    showMessage('Info', message, 'info');
  }

  // --- Render functions (all kept, only changed col-6 -> col-12 sm:col-6, buttons responsive) ---

  const renderUserField = (formik: any) => (
    <div className="col-12">
      <div className="field px-3">
        <label htmlFor="customerId" className="required-label" style={{ fontWeight: 'bold' }}>
          User Name/Mobile Number <span style={{ color: "red", fontSize: "24px" }}>*</span>
        </label>
        <div onMouseEnter={() => setHoveredField('customerId')} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle('customerId')}>
          <Dropdown
            placeholder="Select User"
            id="customerId"
            name="customerId"
            options={orderUser}
            optionLabel="detail"
            optionValue="userID"
            value={formik.values.customerId}
            onChange={handlePhoneDropdownChange}
            onFilter={handlePhoneDropdownFilter}
            filter
            className="w-full"
            style={{ border: 'none', outline: 'none', width: '100%' }}
          />
        </div>
        {formik.errors.customerId && formik.touched.customerId && (
          <small className="p-error">{formik.errors.customerId as string}</small>
        )}
      </div>
    </div>
  );

  const renderDescriptionField = (formik: any) => (
    <div className="field col-12 mb-3 px-3">
      <label htmlFor="description" style={{ fontWeight: 'bold' }}>Description<span style={{ color: "red", fontSize: "24px" }}>*</span></label>
      <div onMouseEnter={() => setHoveredField('description')} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle('description')}>
        <InputTextarea
          placeholder="Enter Description"
          name="description"
          id="description"
          rows={5}
          className="w-full"
          value={formik.values.description}
           onChange={(e) => {
                          const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                           formik.setFieldValue('description', cleanedValue);
                          }}
          style={{border: 'none',outline: 'none',width: '100%',padding: '0.75rem',fontSize: '1rem'}}
        />
      </div>
      {formik.errors.description && formik.touched.description && (
        <small className="p-error">{formik.errors.description as string}</small>
      )}
    </div>
  );

  const renderSeverityField = (formik: any) => (
    <div className="col-12 sm:col-6">
      <div className="field mb-3 px-3">
        <label htmlFor="severity" style={{ fontWeight: 'bold' }}>Severity</label>
        <div onMouseEnter={() => setHoveredField('severity')} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle('severity')}>
          <Dropdown
            placeholder="Select Severity"
            name="severity"
            id="severity"
            options={severityItems}
            optionLabel="name"
            optionValue="name"
            onChange={(e) => handleSeverityChange(e, formik)}
            value={formik.values.severity}
            className="w-full"
            style={{ border: 'none', outline: 'none', width: '100%' }}
          />
        </div>
      </div>
    </div>
  );

  const renderRemarksField = (formik: any) => (
    <div className="field col-12 mb-3 px-3">
      <label htmlFor="remarks" style={{ fontWeight: 'bold' }}>Remarks</label>
      <div onMouseEnter={() => setHoveredField('remarks')} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle('remarks')}>
        <InputTextarea
          placeholder="Enter Remarks"
          name="remarks"
          id="remarks"
          rows={3}
          className="w-full"
          value={formik.values.remarks || ''}
           onChange={(e) => {
                          const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                           formik.setFieldValue('remarks', cleanedValue);
                          }}
          style={{border: 'none',outline: 'none',width: '100%',padding: '0.75rem'}}
        />
      </div>
    </div>
  );

  const renderCustomerSupportActionField = (formik: any) => (
    <div className="field col-12 mb-3 px-3">
      <label htmlFor="customerSupportAction" style={{ fontWeight: 'bold' }}>Customer Support Action</label>
      <div onMouseEnter={() => setHoveredField('customerSupportAction')} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle('customerSupportAction')}>
        <InputTextarea
          placeholder="Enter Customer Support Action"
          name="customerSupportAction"
          id="customerSupportAction"
          rows={4}
          className="w-full"
          value={formik.values.customerSupportAction || ''}
           onChange={(e) => {
                          const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                           formik.setFieldValue('customerSupportAction', cleanedValue);
                          }}
          style={{border: 'none',outline: 'none',width: '100%',padding: '0.75rem'}}
        />
      </div>
    </div>
  );

  const renderButtons = (formik: any, isSubmitted: boolean) => (
    <div className="flex justify-content-center gap-3 mt-4">
      <Button label="Submit" icon="pi pi-check" loading={isSubmitted} className="p-button-primary w-full sm:w-auto" type="submit" />
      <Button label="Reset" icon="pi pi-refresh" type="reset" className="p-button-secondary w-full sm:w-auto" onClick={() => formik.resetForm()} />
    </div>
  );

  return (
    <div className="card">
      <Toast ref={toast} />
      <div className="text-center">
        <h2 style={{ color: "green", marginBottom: "1rem", marginTop: "1rem" }}>Complaint Register</h2>
      </div>

      <div style={formContainerStyle}>
        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          {/* Product Complaint */}
          <TabPanel header="Product Complaint">
            <form onSubmit={productFormik.handleSubmit} className="p-fluid">
              <div className="formgrid grid">
                {renderUserField(productFormik)}

                <div className="col-12 sm:col-6">
                  <div className="field px-3">
                    <label htmlFor="invoiceId" className="required-label" style={{ fontWeight: 'bold' }}>
                      Invoice Number <span style={{ color: "red", fontSize: "24px" }}>*</span>
                    </label>
                    <div onMouseEnter={() => setHoveredField('invoiceId')} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle('invoiceId')}>
                      <CascadeSelect
                        value={productFormik.values.invoiceId}
                        options={invoiceHierarchy}
                        optionLabel="name"
                        optionGroupLabel="name"
                        optionGroupChildren={["months", "invoices"]}
                        style={{ minWidth: '100%' }}
                        placeholder="Select Invoice"
                        onChange={handleInvoiceChange}
                        className="w-full"
                      />
                    </div>
                    {productFormik.errors.invoiceId && productFormik.touched.invoiceId && (
                      <small className="p-error">{productFormik.errors.invoiceId as string}</small>
                    )}
                  </div>
                </div>

                <div className="col-12 sm:col-6">
                  <div className="field px-3">
                    <label htmlFor="productId" className="required-label" style={{ fontWeight: 'bold' }}>
                      Product Name <span style={{ color: "red", fontSize: "24px" }}>*</span>
                    </label>
                    <div onMouseEnter={() => setHoveredField('productId')} onMouseLeave={() => setHoveredField(null)} style={fieldHoverStyle('productId')}>
                      <Dropdown
                        placeholder="Select Product"
                        name="productId"
                        id="productId"
                        options={orderProduct}
                        optionLabel="detail"
                        optionValue="productId"
                        value={productFormik.values.productId}
                        onChange={handleProductChange}
                        className="w-full"
                        style={{ border: 'none', outline: 'none', width: '100%' }}
                      />
                    </div>
                    {productFormik.errors.productId && productFormik.touched.productId && (
                      <small className="p-error">{productFormik.errors.productId as string}</small>
                    )}
                  </div>
                </div>

                {renderDescriptionField(productFormik)}
                {renderSeverityField(productFormik)}
                {renderRemarksField(productFormik)}
                {renderCustomerSupportActionField(productFormik)}

                {/* File Upload */}
                  <ImageTemplateDemo 
                    onFileUpload={handelfileUpload} 
                    handelFileSize={handelfileSizeExceeded} 
                  />
              </div>

              {renderButtons(productFormik, isProductSubmitted)}
            </form>
          </TabPanel>

          {/* Service Complaint */}
          <TabPanel header="Service Complaint">
            <form onSubmit={serviceFormik.handleSubmit} className="p-fluid">
              <div className="formgrid grid">
                {renderUserField(serviceFormik)}
                {renderDescriptionField(serviceFormik)}
                  <ImageTemplateDemo 
                    onFileUpload={handelfileUpload} 
                    handelFileSize={handelfileSizeExceeded} 
                  />
                {renderSeverityField(serviceFormik)}
                {renderRemarksField(serviceFormik)}
                {renderCustomerSupportActionField(serviceFormik)}
              </div>
              {renderButtons(serviceFormik, isServiceSubmitted)}
            </form>
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
};

export default ComplaintRegister;
